/**
 * 账单上传抽屉 — 统一上传组件
 *
 * 被两个页面共享:
 *   - ApInvBatchListPage（批次管理 / AP 对账工作台）
 *   - InvoiceDataSourcePage（账单批次上传）
 *
 * 使用模式:
 *   1. 完整模式（新建批次）: 需填写供应商/费用类型/批次号/机场 + 上传文件
 *   2. 追加模式（已有批次）: 配置从 props 带入并锁定，只上传文件
 *
 * 上传来源类型（source_type）:
 *   - INVOICE_UPLOAD: 常规账单上传，支持自动解析和结算触发
 *   - PARTNER_BILLING: 合作方账单，需选择合作方，自动创建 portal_publication
 *
 * 文件处理流水线:
 *   阶段1(本地预检) → 阶段2(OSS临时上传) → 阶段3(用户编排) → 阶段4(提交注册后端转正)
 *
 * 关键机制:
 *   - OSS 上传使用 is_temporary=true，未提交的文件由 OSS 生命周期规则自动清理
 *   - 全抽屉拖放区：整个 Drawer 区域支持文件拖入
 *   - 三元组批次检查：供应商+费用类型+批次号 变化时自动检查是否存在
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
    Drawer,
    Form,
    Button,
    Select,
    message,
    Space,
    Alert,
    Row,
    Col,
    Input,
    Tooltip,
    Typography,
    Segmented,
    Switch,
    Tag,
    Modal,
    Collapse,
} from "antd";
import { InfoCircleOutlined, InboxOutlined, ScissorOutlined } from "@ant-design/icons";
import { settlementService } from "../../../../../api/enhancedApi";
import settlementApi from "../../../../../api/settlementApi";
import { businessPartnerApi } from "../../../../../api/contractManagementApi";
import { STANDARD_COST_TYPE_OPTIONS } from "../../../constants";
import AirportSelector from "../../../../../components/common/AirportSelector";
import FileDropZone from "./FileDropZone";
import SortableFileList from "./SortableFileList";
import { submitPayloadContainsPdf, useFileUploadManager } from "./useFileUploadManager";
import FileSplitModal from "../../../InvoiceDataSource/components/FileSplitModal";
import OcrDpiSlider, { OCR_DPI_DEFAULT, normalizeOcrDpi } from "../OcrDpiSlider";
import "./UploadDrawer.css";

const { Text } = Typography;

const UPLOAD_DRAWER_LAST_VALUES_KEY = "workbench_upload_drawer_last_values";

export const resolveUploadHighResolutionPdfOcr = (requested, submitPayload, isPartnerBilling = false) => (
    !isPartnerBilling && Boolean(requested) && submitPayloadContainsPdf(submitPayload)
);

export const resolveUploadPdfRenderDpi = (requested, pdfRenderDpi, submitPayload, isPartnerBilling = false) => (
    resolveUploadHighResolutionPdfOcr(requested, submitPayload, isPartnerBilling)
        ? normalizeOcrDpi(pdfRenderDpi)
        : undefined
);

export const UploadOcrAdvancedSettings = ({ hasPdf }) => {
    if (!hasPdf) return null;
    return (
        <Collapse
            ghost
            size="small"
            items={[
                {
                    key: "advanced-ocr",
                    label: "高级识别设置",
                    children: (
                        <Form.Item label="OCR 选项" style={{ marginBottom: 8 }}>
                            <Space align="center" wrap>
                                <Form.Item name="high_resolution_pdf_ocr" valuePropName="checked" noStyle>
                                    <Switch aria-label="高清识别扫描 PDF" />
                                </Form.Item>
                                <span>高清识别扫描 PDF</span>
                                <Text type="secondary">处理速度较慢，并会保存为这些账单后续 OCR 的默认模式</Text>
                            </Space>
                            <Form.Item noStyle shouldUpdate={(prev, next) => prev.high_resolution_pdf_ocr !== next.high_resolution_pdf_ocr}>
                                {({ getFieldValue }) => (
                                    getFieldValue("high_resolution_pdf_ocr") ? (
                                        <Form.Item name="pdf_render_dpi" style={{ marginBottom: 0 }}>
                                            <OcrDpiSlider />
                                        </Form.Item>
                                    ) : null
                                )}
                            </Form.Item>
                        </Form.Item>
                    ),
                },
            ]}
        />
    );
};

const UploadDrawer = ({
    open,
    onClose,
    onSuccess,
    presetBatchNumber = null,
    presetVendorId = null,
    presetStandardCostType = null,
    presetAirportCode = null,
}) => {
    const [form] = Form.useForm();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [suppliers, setSuppliers] = useState([]);
    const [loadingSuppliers, setLoadingSuppliers] = useState(false);

    // 上传模式：INVOICE_UPLOAD（账单上传）| PARTNER_BILLING（合作方账单）
    const [uploadMode, setUploadMode] = useState("INVOICE_UPLOAD");
    const isPartnerBilling = uploadMode === "PARTNER_BILLING";

    // 合作方（CUSTOMER）列表
    const [customers, setCustomers] = useState([]);
    const [loadingCustomers, setLoadingCustomers] = useState(false);

    // 拆分弹窗
    const [splittingFile, setSplittingFile] = useState(null);
    const [isSplitModalVisible, setIsSplitModalVisible] = useState(false);

    // 全抽屉拖放
    const [isDrawerDragOver, setIsDrawerDragOver] = useState(false);
    const drawerDragCounter = useRef(0);

    // 三元组批次检查
    const [batchCheckResult, setBatchCheckResult] = useState(null); // { exists, ids_count, vendor_name }
    const batchCheckTimer = useRef(null);

    const manager = useFileUploadManager();
    const isAppendMode = !!(presetBatchNumber && presetVendorId && presetStandardCostType);

    // 统计 NEEDS_SPLIT 文件
    const needsSplitFiles = useMemo(() => {
        return Array.from(manager.files.values()).filter((f) => f.phase === "NEEDS_SPLIT");
    }, [manager.files]);
    const hasPdfUpload = manager.hasSubmittablePdf;

    useEffect(() => {
        if (!hasPdfUpload) {
            form.setFieldValue("high_resolution_pdf_ocr", false);
        }
    }, [form, hasPdfUpload]);

    // =========================================================================
    // 初始化
    // =========================================================================
    useEffect(() => {
        if (!open) return;
        loadSuppliers();
        loadCustomers();
        setBatchCheckResult(null);
        setUploadMode("INVOICE_UPLOAD");

        if (isAppendMode) {
            form.setFieldsValue({
                supplier: presetVendorId,
                cost_type: presetStandardCostType,
                batch_number: presetBatchNumber,
                airport_code: presetAirportCode,
                parse_immediately: true,
                auto_trigger_settlement: true,
                high_resolution_pdf_ocr: false,
                pdf_render_dpi: OCR_DPI_DEFAULT,
            });
        } else {
            try {
                const saved = localStorage.getItem(UPLOAD_DRAWER_LAST_VALUES_KEY);
                if (saved) {
                    const values = JSON.parse(saved);
                    form.setFieldsValue({
                        supplier: values.supplier,
                        cost_type: values.cost_type,
                        airport_code: values.airport_code,
                    });
                }
            } catch { /* ignore */ }
        }
    }, [open, isAppendMode, form, presetBatchNumber, presetVendorId, presetStandardCostType, presetAirportCode]);

    const loadSuppliers = async () => {
        setLoadingSuppliers(true);
        try {
            const response = await businessPartnerApi.getSuppliers({
                partner_type: "SUPPLIER",
                is_active: true,
                page: 1,
                page_size: 500,
            });

            if (response.data && Array.isArray(response.data.items)) {
                setSuppliers(
                    response.data.items.map((supplier) => ({
                        value: supplier.id,
                        label: supplier.code ? `${supplier.name} (${supplier.code})` : supplier.name,
                        code: supplier.code,
                    })),
                );
            }
        } catch (err) {
            console.error("[UploadDrawer] 加载供应商失败:", err);
            message.error("加载供应商列表失败");
        } finally {
            setLoadingSuppliers(false);
        }
    };

    // 加载合作方（CUSTOMER 类型）列表
    const loadCustomers = async () => {
        setLoadingCustomers(true);
        try {
            const response = await businessPartnerApi.getCustomers({
                is_active: true,
            });
            const list = Array.isArray(response.data) ? response.data : (response.data?.items || []);
            setCustomers(
                list.map((c) => ({
                    value: c.id,
                    label: c.code ? `${c.name} (${c.code})` : c.name,
                })),
            );
        } catch (error) {
            console.error("[UploadDrawer] 加载合作方列表失败:", error);
        } finally {
            setLoadingCustomers(false);
        }
    };

    // =========================================================================
    // 三元组批次检查
    // =========================================================================
    const checkBatchExists = useCallback(async () => {
        if (isAppendMode) return;

        const values = form.getFieldsValue(["supplier", "cost_type", "batch_number"]);
        const { supplier, cost_type, batch_number } = values;

        // 至少需要供应商和费用类型才有意义
        if (!supplier || !cost_type) {
            setBatchCheckResult(null);
            return;
        }

        try {
            const params = {
                search: batch_number?.trim() || "",
                page_size: 5,
            };
            const res = await settlementApi.listBatches(params);
            const batches = res.data?.items || res.items || [];

            // 在结果中检查是否有匹配三元组
            const match = batches.find((b) =>
                b.vendor_id === supplier &&
                b.standard_cost_type === cost_type &&
                (batch_number ? b.batch_number === batch_number.trim() : false)
            );

            if (match) {
                setBatchCheckResult({
                    exists: true,
                    ids_count: match.ids_count || 0,
                    vendor_name: match.vendor_name,
                    batch_number: match.batch_number,
                    vendor_id: match.vendor_id,
                    standard_cost_type: match.standard_cost_type,
                });
            } else {
                setBatchCheckResult({ exists: false });
            }
        } catch {
            setBatchCheckResult(null);
        }
    }, [form, isAppendMode]);

    const debouncedCheckBatch = useCallback(() => {
        if (batchCheckTimer.current) clearTimeout(batchCheckTimer.current);
        batchCheckTimer.current = setTimeout(checkBatchExists, 500);
    }, [checkBatchExists]);

    // =========================================================================
    // 批次号推荐
    // =========================================================================
    const generateBatchNumber = useCallback(() => {
        const now = new Date();
        const pad = (n) => String(n).padStart(2, "0");
        return `BATCH_${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
    }, []);

    // =========================================================================
    // 全抽屉拖放
    // =========================================================================
    const handleDrawerDragEnter = useCallback((e) => {
        // 只响应从外部拖入的文件（非 dnd-kit 内部拖拽）
        if (!e.dataTransfer?.types?.includes("Files")) return;
        e.preventDefault();
        e.stopPropagation();
        drawerDragCounter.current++;
        if (drawerDragCounter.current === 1) setIsDrawerDragOver(true);
    }, []);

    const handleDrawerDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        drawerDragCounter.current--;
        if (drawerDragCounter.current === 0) setIsDrawerDragOver(false);
    }, []);

    const handleDrawerDragOver = useCallback((e) => {
        if (!e.dataTransfer?.types?.includes("Files")) return;
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDrawerDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        drawerDragCounter.current = 0;
        setIsDrawerDragOver(false);

        const files = e.dataTransfer.files;
        if (files && files.length > 0 && !isSubmitting) {
            manager.addFiles(files);
        }
    }, [isSubmitting, manager]);

    // =========================================================================
    // 拆分操作
    // =========================================================================
    const handleSplitFile = useCallback((file) => {
        setSplittingFile({
            filename: file.name,
            rawFile: file.rawFile,
            page_count: file.pageCount,
            fileId: file.id,
        });
        setIsSplitModalVisible(true);
    }, []);

    const handleSplitComplete = useCallback(async (originalFile, subFiles) => {
        try {
            await manager.addSplitFiles(splittingFile.fileId, subFiles);
            message.success(`拆分完成，已生成 ${subFiles.length} 个子文件`);
        } catch (err) {
            message.error("拆分文件处理失败: " + err.message);
        } finally {
            setIsSplitModalVisible(false);
            setSplittingFile(null);
        }
    }, [manager, splittingFile]);

    // =========================================================================
    // 提交
    // =========================================================================
    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            if (!manager.canSubmit) {
                message.warning("请至少上传一个文件");
                return;
            }

            // 温和提醒：有 NEEDS_SPLIT 文件时确认
            if (needsSplitFiles.length > 0) {
                const confirmed = await new Promise((resolve) => {
                    Modal.confirm({
                        title: "部分文件建议拆分",
                        icon: <ScissorOutlined style={{ color: "#fa8c16" }} />,
                        content: (
                            <div>
                                <p>有 {needsSplitFiles.length} 个 PDF 文件页数较多（超过 10 页），建议拆分后再提交以获得更好的解析效果。</p>
                                <p style={{ color: "#999", fontSize: 12 }}>未拆分的文件将不会被提交，不影响已就绪的文件。</p>
                            </div>
                        ),
                        okText: "继续提交",
                        cancelText: "返回拆分",
                        onOk: () => resolve(true),
                        onCancel: () => resolve(false),
                    });
                });
                if (!confirmed) return;
            }

            if (manager.hasUploading) {
                message.warning("部分文件正在上传中，请等待完成");
                return;
            }

            setIsSubmitting(true);


            const filePayload = manager.submitPayload;
            const highResolutionPdfOcr = resolveUploadHighResolutionPdfOcr(
                values.high_resolution_pdf_ocr,
                filePayload,
                isPartnerBilling,
            );
            const pdfRenderDpi = resolveUploadPdfRenderDpi(
                values.high_resolution_pdf_ocr,
                values.pdf_render_dpi,
                filePayload,
                isPartnerBilling,
            );

            // 上传时使用最新的（可能已修改的）batch_number 和 airport_code
            const registerData = {
                vendor_id: values.supplier,
                standard_cost_type: values.cost_type,
                files: filePayload.files,
                file_groups: filePayload.file_groups,
                parse_immediately: isPartnerBilling ? false : values.parse_immediately,
                auto_trigger_settlement: isPartnerBilling ? false : values.auto_trigger_settlement,
                high_resolution_pdf_ocr: highResolutionPdfOcr,
                batch_number: values.batch_number?.trim() || undefined,
                airport_code: isPartnerBilling ? undefined : (values.airport_code || undefined),
                source_type: uploadMode,
            };
            if (highResolutionPdfOcr) {
                registerData.pdf_render_dpi = pdfRenderDpi;
            }

            const registerResult = await settlementService.batchRegisterInvoiceDataSources(registerData);

            // PARTNER_BILLING 模式：上传成功后自动创建 portal_publication 关联记录
            if (isPartnerBilling && values.customer_partner_id) {
                try {
                    const idsIds = (registerResult.data?.data?.items || []).map((item) => item.id).filter(Boolean);
                    if (idsIds.length > 0) {
                        await settlementService.portalPublication.batchCreate({
                            ids_ids: idsIds,
                            customer_partner_id: values.customer_partner_id,
                        });
                        console.log(`[UploadDrawer] 自动创建 ${idsIds.length} 条门户发布记录，合作方ID=${values.customer_partner_id}`);
                    }
                } catch (pubError) {
                    console.error("[UploadDrawer] 自动创建门户发布记录失败:", pubError);
                    message.warning("文件上传成功，但关联合作方失败，请在列表页手动指定");
                }
            }

            const totalFiles = manager.files.size;
            const groupCount = manager.groups.size;
            const independentCount = filePayload.files.length;
            const recordCount = groupCount + independentCount;

            let successMsg;
            if (isPartnerBilling) {
                successMsg = `成功上传 ${totalFiles} 个合作方账单文件并关联合作方`;
            } else if (groupCount > 0) {
                successMsg = `上传 ${totalFiles} 个文件，创建 ${recordCount} 条记录（${groupCount} 个文件组 + ${independentCount} 个独立文件）`;
            } else {
                successMsg = `上传并注册 ${totalFiles} 个账单数据源`;
            }
            message.success(successMsg);

            if (!isAppendMode) {
                try {
                    localStorage.setItem(UPLOAD_DRAWER_LAST_VALUES_KEY, JSON.stringify({
                        supplier: values.supplier,
                        cost_type: values.cost_type,
                        airport_code: values.airport_code,
                    }));
                } catch { /* ignore */ }
            }

            manager.reset();
            form.resetFields();
            setBatchCheckResult(null);
            onSuccess?.();
            onClose?.();
        } catch (err) {
            console.error("[UploadDrawer] 提交失败:", err);
            message.error("提交失败: " + (err.response?.data?.detail || err.message));
        } finally {
            setIsSubmitting(false);
        }
    };

    // =========================================================================
    // 关闭
    // =========================================================================
    const handleClose = () => {
        if (isSubmitting) {
            message.warning("正在提交中，请稍候...");
            return;
        }
        if (manager.files.size > 0) {
            if (!window.confirm("关闭将丢失已上传但未提交的文件，确认关闭？")) {
                return;
            }
        }
        manager.reset();
        form.resetFields();
        setBatchCheckResult(null);
        setUploadMode("INVOICE_UPLOAD");
        onClose?.();
    };

    // =========================================================================
    // 渲染
    // =========================================================================
    return (
        <>
            <Drawer
                title={isAppendMode ? "追加上传账单" : isPartnerBilling ? "上传合作方账单" : "上传账单 · 新建批次"}
                width={720}
                open={open}
                onClose={handleClose}
                maskClosable={!isSubmitting}
                styles={{ body: { padding: "24px", paddingBottom: 80 } }}
                footer={
                    <div style={{ textAlign: "right" }}>
                        <Space>
                            <Button onClick={handleClose} disabled={isSubmitting}>取消</Button>
                            <Button
                                type="primary"
                                onClick={handleSubmit}
                                loading={isSubmitting}
                                disabled={!manager.canSubmit || manager.hasUploading}
                            >
                                {isSubmitting ? "提交中..." : manager.hasUploading ? "上传中..." : "提交"}
                            </Button>
                        </Space>
                    </div>
                }
            >
                {/* 全抽屉拖放区 */}
                <div
                    onDragEnter={handleDrawerDragEnter}
                    onDragLeave={handleDrawerDragLeave}
                    onDragOver={handleDrawerDragOver}
                    onDrop={handleDrawerDrop}
                    style={{ minHeight: "100%" }}
                >
                    <Form
                        form={form}
                        layout="vertical"
                        initialValues={{
                            supplier: undefined,
                            cost_type: undefined,
                            parse_immediately: true,
                            auto_trigger_settlement: true,
                            high_resolution_pdf_ocr: false,
                            pdf_render_dpi: OCR_DPI_DEFAULT,
                        }}
                        onValuesChange={isAppendMode ? undefined : debouncedCheckBatch}
                    >
                        {/* 模式切换（追加模式下不显示） */}
                        {!isAppendMode && (
                            <div style={{ marginBottom: 16 }}>
                                <Segmented
                                    options={[
                                        { label: "账单上传", value: "INVOICE_UPLOAD" },
                                        { label: "合作方账单", value: "PARTNER_BILLING" },
                                    ]}
                                    value={uploadMode}
                                    onChange={(val) => {
                                        setUploadMode(val);
                                        if (val === "PARTNER_BILLING") {
                                            form.setFieldsValue({ parse_immediately: false, auto_trigger_settlement: false, high_resolution_pdf_ocr: false });
                                        } else {
                                            form.setFieldsValue({ parse_immediately: true, auto_trigger_settlement: true });
                                        }
                                    }}
                                    block
                                />
                                <div style={{ marginTop: 8, fontSize: 12, color: "#999" }}>
                                    来源类型：<Tag color={isPartnerBilling ? "purple" : "blue"}>{isPartnerBilling ? "合作方账单 (PARTNER_BILLING)" : "账单上传 (INVOICE_UPLOAD)"}</Tag>
                                </div>
                            </div>
                        )}

                        {/* 配置区 */}
                        <Alert
                            message={isAppendMode ? "追加上传说明" : isPartnerBilling ? "合作方账单上传说明" : "批量上传说明"}
                            description={
                                isAppendMode
                                    ? "当前为追加模式，供应商/费用类型/批次号/机场均沿用现有配置。只需上传新文件即可。"
                                    : isPartnerBilling
                                        ? "上传原始账单文件以便共享给运营合作方审查。选择合作方后系统自动创建发布记录，上传完成后在列表页确认并发布。"
                                        : "同一批次上传的文件必须属于相同的供应商和费用类型。可选择上传后自动解析文件，解析成功后可自动触发结算处理流程。"
                            }
                            type={isPartnerBilling ? "warning" : "info"}
                            showIcon
                            style={{ marginBottom: 20 }}
                        />

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="supplier"
                                    label="供应商"
                                    rules={[{ required: true, message: "请选择供应商" }]}
                                >
                                    <Select
                                        placeholder="请搜索或选择供应商"
                                        showSearch
                                        loading={loadingSuppliers}
                                        disabled={isAppendMode}
                                        filterOption={(input, option) =>
                                            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                                        }
                                        options={suppliers}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="cost_type"
                                    label="费用类型（预分类）"
                                    rules={[{ required: true, message: "请选择费用类型" }]}
                                >
                                    <Select
                                        placeholder="请选择费用类型（预分类）"
                                        showSearch
                                        disabled={isAppendMode}
                                        options={STANDARD_COST_TYPE_OPTIONS}
                                        filterOption={(input, option) =>
                                            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                                        }
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        {/* 合作方账单模式：合作方选择器 */}
                        {isPartnerBilling && (
                            <Row gutter={16}>
                                <Col span={24}>
                                    <Form.Item
                                        name="customer_partner_id"
                                        label="合作方"
                                        rules={[{ required: true, message: "请选择合作方" }]}
                                    >
                                        <Select
                                            placeholder="请搜索或选择合作方（客户类型）"
                                            showSearch
                                            loading={loadingCustomers}
                                            options={customers}
                                            filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        )}

                        {/* 批次号 + 机场（合作方账单模式下隐藏机场） */}
                        <Row gutter={16}>
                            <Col span={isPartnerBilling ? 24 : 16}>
                                <Form.Item
                                    name="batch_number"
                                    label={
                                        <span>
                                            批次号
                                            <Tooltip title="留空时系统将自动生成批次号">
                                                <InfoCircleOutlined style={{ marginLeft: 4, color: "#1890ff" }} />
                                            </Tooltip>
                                        </span>
                                    }
                                >
                                    <Input
                                        placeholder="请输入批次号（留空自动生成）"
                                        disabled={isAppendMode}
                                        addonAfter={
                                            !isAppendMode && (
                                                <Button
                                                    type="link"
                                                    size="small"
                                                    onClick={() => form.setFieldsValue({ batch_number: generateBatchNumber() })}
                                                    style={{ padding: 0, height: "auto" }}
                                                >
                                                    推荐
                                                </Button>
                                            )
                                        }
                                        maxLength={50}
                                    />
                                </Form.Item>
                            </Col>
                            {!isPartnerBilling && (
                                <Col span={8}>
                                    <Form.Item
                                        name="airport_code"
                                        label={
                                            <span>
                                                关联机场
                                                <Tooltip title="可选，选择与该批次关联的机场">
                                                    <InfoCircleOutlined style={{ marginLeft: 4, color: "#1890ff" }} />
                                                </Tooltip>
                                            </span>
                                        }
                                    >
                                        <AirportSelector
                                            valueType="iata_code"
                                            placeholder="选择机场"
                                            allowClear
                                            disabled={isAppendMode}
                                            style={{ width: "100%" }}
                                        />
                                    </Form.Item>
                                </Col>
                            )}
                        </Row>

                        {/* 解析与结算选项（合作方账单模式下隐藏） */}
                        {!isPartnerBilling && !isAppendMode && (
                            <Row gutter={16} style={{ marginBottom: 8 }}>
                                <Col>
                                    <Form.Item style={{ marginBottom: 0 }}>
                                        <Space align="center">
                                            <Form.Item name="parse_immediately" valuePropName="checked" noStyle>
                                                <Switch />
                                            </Form.Item>
                                            <span>上传后自动识别</span>
                                        </Space>
                                    </Form.Item>
                                </Col>
                                <Col>
                                    <Form.Item style={{ marginBottom: 0 }}>
                                        <Space align="center">
                                            <Form.Item name="auto_trigger_settlement" valuePropName="checked" noStyle>
                                                <Switch />
                                            </Form.Item>
                                            <span>识别成功后自动制表校验</span>
                                        </Space>
                                    </Form.Item>
                                </Col>
                            </Row>
                        )}

                        {!isPartnerBilling && <UploadOcrAdvancedSettings hasPdf={hasPdfUpload} />}

                        {/* 三元组批次检查结果 */}
                        {batchCheckResult && !isAppendMode && (
                            <div className="upload-batch-check">
                                {batchCheckResult.exists ? (
                                    <Alert
                                        message="该批次已存在"
                                        description={
                                            <span>
                                                批次「{batchCheckResult.batch_number}」已有 {batchCheckResult.ids_count} 个账单数据源。提交后文件将追加到该批次。{" "}
                                                <a
                                                    href={`/settlement/ap-workbench?batch=${encodeURIComponent(batchCheckResult.batch_number)}${batchCheckResult.vendor_id ? `&vendor=${batchCheckResult.vendor_id}` : ""}${batchCheckResult.standard_cost_type ? `&costType=${encodeURIComponent(batchCheckResult.standard_cost_type)}` : ""}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    查看该批次 →
                                                </a>
                                            </span>
                                        }
                                        type="warning"
                                        showIcon
                                    />
                                ) : (
                                    <Alert
                                        message="新批次"
                                        description="该供应商+费用类型+批次号组合不存在，将创建新的批次。"
                                        type="success"
                                        showIcon
                                    />
                                )}
                            </div>
                        )}

                        {/* 文件区 */}
                        <div className="upload-drawer__file-section">
                            <FileDropZone
                                onFilesAdded={manager.addFiles}
                                disabled={isSubmitting}
                            />

                            {/* NEEDS_SPLIT 全局提示 */}
                            {needsSplitFiles.length > 0 && (
                                <Alert
                                    className="upload-needs-split-alert"
                                    type="warning"
                                    showIcon
                                    icon={<ScissorOutlined />}
                                    message={`有 ${needsSplitFiles.length} 个 PDF 文件页数较多，建议拆分`}
                                    description={
                                        <span>
                                            超过 10 页的 PDF 建议拆分为多个子文件以获得更好的解析效果。
                                            点击文件右侧的 <ScissorOutlined style={{ color: "#fa8c16" }} /> 按钮即可拆分。
                                        </span>
                                    }
                                />
                            )}

                            <SortableFileList
                                files={manager.files}
                                groups={manager.groups}
                                order={manager.order}
                                onMerge={manager.mergeFiles}
                                onAddToGroup={manager.addToGroup}
                                onRemoveFile={manager.removeFile}
                                onRemoveFromGroup={manager.removeFromGroup}
                                onDisbandGroup={manager.disbandGroup}
                                onRenameGroup={manager.renameGroup}
                                onSplitFile={handleSplitFile}
                            />
                        </div>
                    </Form>

                    {/* 全抽屉拖放覆盖层 */}
                    {isDrawerDragOver && (
                        <div className="upload-drawer-drop-overlay">
                            <div className="upload-drawer-drop-overlay__content">
                                <InboxOutlined className="upload-drawer-drop-overlay__icon" />
                                <Text strong style={{ fontSize: 16, color: "#1890ff" }}>释放文件以开始上传</Text>
                                <Text type="secondary">支持 PDF、Excel、Word、图片</Text>
                            </div>
                        </div>
                    )}
                </div>
            </Drawer>

            <FileSplitModal
                open={isSplitModalVisible}
                file={splittingFile}
                onConfirm={handleSplitComplete}
                onCancel={() => {
                    setIsSplitModalVisible(false);
                    setSplittingFile(null);
                }}
            />
        </>
    );
};

export default UploadDrawer;
